import { Component, Injectable, inject } from "@angular/core";
import {
    FormControl,
    FormGroupDirective,
    FormsModule,
    NgForm,
    ReactiveFormsModule,
    Validators
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";
import { lastValueFrom } from "rxjs";
import { CustomersFields } from "src/app/features";
import { AlertDialogService } from "../alert-dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { restManager } from "app/ajs-upgraded-providers";

export type SendQrcodeDialogData = { customer?: CustomersFields };

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

@Component({
    selector: 'app-send-qrcode-dialog',
    templateUrl: './send-qrcode-dialog.component.html',
    styleUrls: ['./send-qrcode-dialog.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TranslateModule,
        ReactiveFormsModule
    ]
})
export class SendQrcodeDialogComponent {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly translateService = inject(TranslateService);
    private readonly restManagerService = inject(restManager);
    private readonly matDialogRef = inject(MatDialogRef);

    protected readonly data: SendQrcodeDialogData = inject(MAT_DIALOG_DATA);

    emailFormControl = new FormControl(this.data.customer?.email, [Validators.required, Validators.email]);
    matcher = new MyErrorStateMatcher();
    isDisableConfirmButton!: boolean;

    constructor() {
        const timeQrcodeButtonDisabled = localStorage.getItem('qrcodeButtonDisabled');
        if (timeQrcodeButtonDisabled) {
            const timePassed = Date.now() - parseInt(timeQrcodeButtonDisabled);
            if (timePassed < 10000) {
                this.isDisableConfirmButton = true;
                setTimeout(() => {
                    this.isDisableConfirmButton = false;
                    localStorage.removeItem('qrcodeButtonDisabled');
                }, 10000 - timePassed);
            } else {
                localStorage.removeItem('qrcodeButtonDisabled');
            }
        }
    }

    protected async confirm() {
        if (this.emailFormControl.invalid) {
            return;
        };

        try {
            await this.restManagerService.post(`customers/${this.data.customer?.id}/send_welcome_email`, {
                "email": this.emailFormControl.value,
                "name": this.data.customer?.company_name || `${this.data.customer?.first_name} ${this.data.customer?.last_name}`
            });
        } catch (err) {
            this.alertDialogService.openDialog({
                data: {
                    messageLabel: this.translateService.instant('CUSTOMERS.TOPBAR.ACTIONS.ERROR_SEND_QRCODE')
                }
            });
        }

        this.isDisableConfirmButton = true;
        localStorage.setItem('qrcodeButtonDisabled', String(Date.now()));

        this.matDialogRef.close();
    }
}


@Injectable({
    providedIn: "root",
})
export class SendQrCodeDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(config?: NonNullableConfigData<SendQrcodeDialogData>) {
        const data: SendQrcodeDialogData = config?.data || { customer: undefined };
        const configEdited: NonNullableConfigData<SendQrcodeDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions({ width: '400px' }),
            disableClose: true,
            data,
        };
        return lastValueFrom(
            this.dialogRef.open(SendQrcodeDialogComponent, configEdited).afterClosed()
        );
    }
}
